import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import { Image } from '~/components/Image';
import Typography from '~/components/Typography';
import type { Author } from '~/interfaces/author';
import type { HomePage } from '~/interfaces/home_page';
import type { Publication } from '~/interfaces/publication';

import PopoverHelper from './PopoverHelper';

interface Props {
  publication: Publication;
  authors: Author[];
  homePage: HomePage;
}

const AuthorsWidget = ({ publication, authors, homePage }: Props) => {
  const { t } = useTranslation();
  const hasNoAuthors = authors.length === 0;
  const isOneAuthor = authors.length === 1;
  const isTwoAuthors = authors.length === 2;
  const isMoreThanTwoAuthors = authors.length > 2;
  const labelText = homePage.main_section.authors_widget.label || t('author.byline');
  const authorText = homePage.main_section.authors_widget.text || '';
  const descriptionText = homePage.main_section.authors_widget.text;
  const emailSenderName = publication.email_sender_name;

  const renderTwoAuthorsDescription = () => {
    if (authorText) return authorText;
    if (emailSenderName) return emailSenderName;
    if (descriptionText) return descriptionText;

    return `${authors[0].name} & ${authors[1].name}`;
  };

  return (
    <div className="z-20">
      <PopoverHelper
        bgColor={homePage.main_section.authors_widget.dropdown_bg_color}
        textColor={homePage.main_section.authors_widget.dropdown_text_color}
        authors={authors}
        isMoreThanTwoAuthors={isMoreThanTwoAuthors}
      >
        <div className={cx(isMoreThanTwoAuthors ? 'flex flex-col space-y-2' : 'flex space-x-2')}>
          <div>
            {isOneAuthor && (
              <div className="flex -space-x-1 overflow-hidden">
                {authors.map((author: any) => {
                  return (
                    <div className="h-5 w-5 overflow-hidden rounded-full" key={author.id}>
                      <Image
                        loading="eager"
                        src={author.profile_picture.thumb.url}
                        alt={`Profile thumb for author ${author.name}`}
                        maxWidth={100}
                        aspectRatio="square"
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {isTwoAuthors && (
              <div className="flex -space-x-1 overflow-hidden">
                {authors.map((author: any) => {
                  return (
                    <img
                      key={author.id}
                      className="inline-block h-5 w-5 rounded-full"
                      loading={'eager'}
                      src={author.profile_picture.thumb.url}
                      alt={`Profile thumb for author ${author.name}`}
                    />
                  );
                })}
              </div>
            )}
            {isMoreThanTwoAuthors && (
              <div className="flex -space-x-1 overflow-hidden">
                {authors.map((author: any, index: any) => {
                  if (index < 3) {
                    return (
                      <img
                        key={author.id}
                        className="inline-block h-5 w-5 rounded-full"
                        src={author.profile_picture.thumb.url}
                        loading={'eager'}
                        alt={`Profile thumb for author ${author.name}`}
                      />
                    );
                  }

                  return null;
                })}
                {authors.length > 3 && (
                  <div className="inline-block h-5 w-5 items-center justify-center rounded-full bg-gray-300">
                    <span className="text-xs font-semibold text-white">+</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            {!hasNoAuthors && (
              <Typography
                as="span"
                fontFamily={homePage.general_section.theme.font}
                color={homePage.main_section.authors_widget.text_color}
                className="mb-0.5 whitespace-nowrap text-xs font-semibold"
              >
                {labelText}
              </Typography>
            )}
            <div className="flex">
              {isOneAuthor && (
                <Typography
                  as="span"
                  size="xs"
                  weight="light"
                  fontFamily={homePage.general_section.theme.font}
                  color={homePage.main_section.authors_widget.text_color}
                  className="mr-0.5 line-clamp-1 opacity-75"
                >
                  {authorText || emailSenderName || descriptionText || authors[0].name}
                </Typography>
              )}
              {isTwoAuthors && (
                <Typography
                  as="span"
                  size="xs"
                  weight="light"
                  fontFamily={homePage.general_section.theme.font}
                  color={homePage.main_section.authors_widget.text_color}
                  className="mr-0.5 line-clamp-1 opacity-75"
                >
                  {renderTwoAuthorsDescription()}
                </Typography>
              )}
              {isMoreThanTwoAuthors && (
                <Typography
                  as="span"
                  size="xs"
                  weight="light"
                  fontFamily={homePage.general_section.theme.font}
                  color={homePage.main_section.authors_widget.text_color}
                  className="mr-0.5 line-clamp-1 opacity-75"
                >
                  {authorText || emailSenderName || descriptionText || publication.name}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </PopoverHelper>
      {hasNoAuthors && (
        <div className="flex flex-col">
          <Typography
            as="span"
            fontFamily={homePage.general_section.theme.font}
            color={homePage.main_section.authors_widget.text_color}
            className="mb-0.5 whitespace-nowrap text-xs font-semibold"
          >
            {labelText}
          </Typography>
          <Typography
            as="span"
            size="xs"
            fontFamily={homePage.general_section.theme.font}
            color={homePage.main_section.authors_widget.text_color}
            weight="light"
            className="mr-0.5 line-clamp-1 opacity-75"
          >
            {authorText || emailSenderName || publication.name}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default AuthorsWidget;
